import { ComponentProps, ReactElement, useContext, useEffect, useState } from 'react';
import { Dayts, formatNumber, getClasses } from '@/utils';
import { InvoiceTrip, InvoiceTripTypeEnum } from '@/models/gen/graphql';

import { INVALID_DATE_STRING } from '@/constants';
import InvoicePdfReadyContext from '@/features/PdfLayout/layouts/Invoice/InvoicePdfTable/InvoicePdfReadyContext';
import Table from '@/common/Table';

export type TripRowProps = ComponentProps<typeof Table.Row> & {
  trip: Partial<InvoiceTrip>;
  types?: InvoiceTripTypeEnum[];
  hasDiscounts?: number;
  showCrewId?: boolean;
  requiresNames?: boolean;
};

const InvoicePdfTableRow = ({ trip, types = [], hasDiscounts, showCrewId, requiresNames, ...props }: TripRowProps): ReactElement => {
  const { notifyCombinedGroupRendered } = useContext(InvoicePdfReadyContext);
  const isLastCombinedRow = props.className?.includes('LastCombinedRow');
  const [combinedRendered, setCombinedRendered] = useState(false);

  // Two-phase render: mirrors SubtotalRow's pattern. First render sets combinedRendered=true,
  // second render calls notifyCombinedGroupRendered so the gate fires after actual content is painted.
  useEffect(() => {
    if (!isLastCombinedRow) return;
    if (!combinedRendered) {
      setCombinedRendered(true);
      return;
    }
    notifyCombinedGroupRendered();
  }, [isLastCombinedRow, combinedRendered, notifyCombinedGroupRendered]);

  return types.length > 0 && !types.includes(trip.type) && !!trip.combineId ? (
    <CombinedTripRow {...props} trip={trip} />
  ) : (
    <TripRow {...props} hasDiscounts={hasDiscounts} trip={trip} showCrewId={showCrewId} requiresNames={requiresNames} />
  );
};

export const TripRow = ({ trip, hasDiscounts, requiresNames, showCrewId, ...props }: TripRowProps): ReactElement => {
  const displayParts = [trip?.crewId?.toString?.()];
  if (requiresNames) displayParts.push(trip?.firstName, trip?.lastName);
  const crewIdDisplay = displayParts.filter(Boolean).join(' ').trim();

  return (
    <Table.Row {...props}>
      <Table.Cell className="INDEX" data-placeholder="0">
        {trip?.index}
      </Table.Cell>
      <Table.Cell className="TYPE" data-placeholder="--">
        {trip?.type?.toUpperCase()}
      </Table.Cell>
      <Table.Cell className="DATE" data-placeholder="--">
        {new Dayts(trip?.scheduled).formatAs(Dayts.Format.DATE_FE_SHORT)}
      </Table.Cell>
      <Table.Cell className="SCHEDULED" data-placeholder="--">
        {new Dayts(trip?.scheduled).formatAs(Dayts.Format.TIME)}
      </Table.Cell>
      {!!hasDiscounts && (
        <Table.Cell className="CURB_TIME" data-placeholder="--">
          {trip?.importTime
            ? trip?.importTime === INVALID_DATE_STRING
              ? new Dayts(trip?.scheduled).formatAs(Dayts.Format.TIME)
              : new Dayts(trip?.importTime).formatAs(Dayts.Format.TIME)
            : ''}
        </Table.Cell>
      )}
      <Table.Cell className="CITY" data-placeholder="--">
        {trip?.airportCode}
      </Table.Cell>
      <Table.Cell className="AIRLINE" data-placeholder="--">
        {trip?.servicerIataAirlineCode}
      </Table.Cell>
      <Table.Cell className="FLIGHT_NUMBER" data-placeholder="--">
        {trip?.name || trip?.flightNumber.toString().padStart(4, '0')}
      </Table.Cell>
      <Table.Cell className="PILOTS" data-placeholder="0">
        {trip?.pilots}
      </Table.Cell>
      <Table.Cell className="ATTENDANTS" data-placeholder="0">
        {trip?.attendants}
      </Table.Cell>
      {!!showCrewId && (
        <Table.Cell className="CREW_ID" data-placeholder="--">
          {crewIdDisplay}
        </Table.Cell>
      )}
      <Table.Cell className="PICKUP_LOCATION" data-placeholder="--">
        {trip?.puLocation}
      </Table.Cell>
      <Table.Cell className="DROPOFF_LOCATION" data-placeholder="--">
        {trip?.doLocation}
      </Table.Cell>
      {!!hasDiscounts && (
        <Table.Cell className="SAVINGS" style={{ textAlign: 'right' }} data-placeholder="--" data-prefix="$">
          {trip?.savings && trip?.savings > 0 ? formatNumber.currency(trip?.savings) : ''}
        </Table.Cell>
      )}
      <Table.Cell className="RATE" style={{ textAlign: 'right' }} data-prefix="$">
        {formatNumber.currency(Number(trip?.rate || '0.00'))}
      </Table.Cell>
    </Table.Row>
  );
};

export const CombinedTripRow = ({ trip, ...props }: TripRowProps): ReactElement => {
  return (
    <Table.Row {...props} style={{ ...props.style, display: 'flex', alignItems: 'center' }}>
      <Table.Cell
        style={{ fontSize: 9, fontWeight: 350 }}
      >{`Combined with a trip from a schedule (${trip?.type.toUpperCase()}) ${new Dayts(trip?.scheduled).formatAs(Dayts.Format.DATE_FE_SHORT)} ${new Dayts(trip?.scheduled).formatAs(Dayts.Format.TIME)} ${trip?.servicerIataAirlineCode}${trip?.flightNumber}`}</Table.Cell>
    </Table.Row>
  );
};

export type SubtotalRowProps = ComponentProps<typeof Table.Row> & {
  subtotal: number;
  totalTrips?: number;
  netBillableTrips?: number;
  hasDiscounts?: number;
};

export const SubtotalRow = ({ subtotal, totalTrips, netBillableTrips, hasDiscounts, ...props }: SubtotalRowProps): ReactElement => {
  const { allCombinedGroupsRendered } = useContext(InvoicePdfReadyContext);
  const [rendered, setRendered] = useState(false);
  const isLastRow = props.className?.includes('LastRow');
  const classes = getClasses(props.className, isLastRow && rendered && allCombinedGroupsRendered && 'PdfReady');
  const showNetBillableTrips = hasDiscounts === 1;
  useEffect(() => {
    if (!isLastRow || rendered) return;
    setRendered(true);
  }, [isLastRow, rendered, props.className]);
  return (
    <>
      <Table.Row
        {...props}
        className={classes}
        style={{
          ...props.style,
          borderBottom: totalTrips && !showNetBillableTrips ? '1px solid #EAE2DA' : 'none',
        }}
      >
        <Table.Cell className="COUNT">{totalTrips ? `Total Trips: ${totalTrips}` : ''}</Table.Cell>
        <Table.Cell className="LABEL" colSpan={3}>
          SUB TOTAL:
        </Table.Cell>
        <Table.Cell className="SUBTOTAL" data-prefix="$">
          {formatNumber.currency(subtotal || 0)}
        </Table.Cell>
      </Table.Row>
      {showNetBillableTrips && (
        <Table.Row style={{ ...props.style, borderBottom: '1px solid #EAE2DA' }}>
          <Table.Cell className="COUNT">{`Net Billable Trips: ${netBillableTrips || totalTrips}`}</Table.Cell>
        </Table.Row>
      )}
    </>
  );
};

export default InvoicePdfTableRow;
